import { isQuietWakeupAnswer, isWakeupSeed, type UIChatMessage } from 'models'

export interface WakeupQuietRun {
    /** Checks in the run, each a seed and its quiet answer. */
    count: number
    /** When the last check of the run started, from its seed. */
    lastAt?: number
}

export interface WakeupFeed {
    /** Messages the feed does not draw as bubbles. */
    hidden: Set<string>
    /** By the id of the first seed of a run of quiet checks: the line drawn in its place. */
    quietRuns: Map<string, WakeupQuietRun>
}

/**
 * How a chat with wakeups reads (spec agent-wakeup, "Interfaccia"): a run of
 * checks that found nothing is one line ("nothing new in 5 checks, the last
 * at 14:20") instead of five prompts the user never wrote and five empty
 * answers, and a quiet answer of the user's own turn draws nothing either.
 * A seed whose answer has news stays, drawn as a thin line by the feed.
 *
 * `draws` is the view's own test of whether a message shows anything (a
 * widget, an approval card): such an answer is never folded, whatever its
 * `scheduleWakeup` said.
 */
export function buildWakeupFeed(
    messages: UIChatMessage[],
    draws: (message: UIChatMessage) => boolean = () => false,
): WakeupFeed {
    const isQuiet = (message?: UIChatMessage) =>
        isQuietWakeupAnswer(message) && !draws(message as UIChatMessage)
    const hidden = new Set<string>()
    const quietRuns = new Map<string, WakeupQuietRun>()
    let index = 0
    while (index < messages.length) {
        const message = messages[index]
        if (isWakeupSeed(message) && isQuiet(messages[index + 1])) {
            const start = message.id
            let count = 0
            let lastAt: number | undefined
            while (
                isWakeupSeed(messages[index]) &&
                isQuiet(messages[index + 1])
            ) {
                count += 1
                lastAt = messages[index].metadata?.createdAt ?? lastAt
                hidden.add(messages[index].id)
                hidden.add(messages[index + 1].id)
                index += 2
            }
            quietRuns.set(start, { count, lastAt })
            continue
        }
        if (isQuiet(message)) {
            hidden.add(message.id)
        }
        index += 1
    }
    return { hidden, quietRuns }
}
